import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { DownloadDatasheet } from "@/components/DownloadDatasheet";
import { SPECS, PARAMETERS } from "@/content";

export default function Specs() {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => SPECS.filter((s) => (s.k + " " + s.v).toLowerCase().includes(q.trim().toLowerCase())),
    [q]
  );

  return (
    <section id="specs" data-testid="specs-section" className="relative py-20 lg:py-28 bg-brand-ink overflow-hidden grain">
      <div className="absolute inset-0 dotgrid-light opacity-30" aria-hidden="true" />
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -bottom-10 left-0 font-display font-extrabold text-[19vw] leading-none text-transparent"
        style={{ WebkitTextStroke: "1px rgba(255,255,255,0.07)" }}
      >
        EH8300
      </span>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead
            dark
            eyebrow="Ficha técnica completa"
            title="Cada especificación, a la vista"
            sub="La ficha del Excbio EH8300 VET sin letra pequeña: principios de medición, muestra, QC, almacenamiento y conectividad."
          />
          <Reveal delay={0.15}>
            <label className="relative block w-full lg:w-72" data-testid="specs-search-wrapper">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                data-testid="specs-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filtrar especificaciones…"
                className="w-full rounded-full border border-white/15 bg-white/5 pl-11 pr-4 py-3.5 text-sm font-semibold text-white placeholder:text-slate-500 outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/20 transition"
              />
            </label>
          </Reveal>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-x-10 gap-y-0" data-testid="specs-grid">
          {filtered.map((s, i) => (
            <Reveal key={s.k} delay={Math.min(i * 0.04, 0.3)}>
              <div
                data-testid={`spec-row-${s.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-6 py-5 border-b border-white/10 group"
              >
                <span className="font-mono2 text-[11px] uppercase tracking-[0.18em] text-slate-500 sm:w-56 shrink-0 group-hover:text-brand-orange transition-colors duration-300">
                  {s.k}
                </span>
                <span className="text-[15px] font-semibold text-slate-100 leading-relaxed">{s.v}</span>
              </div>
            </Reveal>
          ))}
          {filtered.length === 0 && (
            <p className="col-span-full py-8 text-center text-slate-400 font-semibold" data-testid="specs-empty-state">
              Sin resultados para “{q}”. Escríbenos por WhatsApp y te respondemos la duda.
            </p>
          )}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 rounded-3xl border border-white/10 bg-white/5 p-6 sm:p-7" data-testid="specs-datasheet-banner">
          <div>
            <span className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-brand-orange">Ficha técnica en PDF</span>
            <p className="mt-2 text-[15px] font-semibold text-slate-100">
              Llévate la ficha completa del EH8300 VET: especificaciones, parámetros, especies y oferta de lanzamiento. 2 páginas · 220 KB.
            </p>
          </div>
          <DownloadDatasheet variant="solid" testid="specs-download-datasheet-button" className="shrink-0 btn-shine" />
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14" data-testid="specs-parameters">
            <span className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-brand-orange">
              Panel de parámetros
            </span>
            <div className="mt-5 flex flex-wrap gap-2">
              {PARAMETERS.map((p) => (
                <span
                  key={p}
                  className="font-mono2 rounded-lg border border-white/15 bg-white/5 px-3.5 py-2 text-xs font-medium text-slate-200 hover:border-brand-orange/60 hover:text-brand-orange transition-colors duration-300 cursor-default"
                >
                  {p}
                </span>
              ))}
              {["Histograma WBC", "Histograma RBC", "Histograma PLT"].map((h) => (
                <span
                  key={h}
                  className="rounded-lg bg-brand-orange/15 border border-brand-orange/40 px-3.5 py-2 text-xs font-bold text-brand-orange"
                >
                  {h}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
